/**
 * TypeScript mirror of the alert-analytics API (`serving/alert_analytics.py`, backed by
 * `db/reports_alert_analytics.sql`). This file IS the frozen contract shared by the
 * backend, this report and the map/drawer work -- change a field name here only together
 * with the SQL function and the route that produce it.
 *
 * Conventions on the wire (plan "Verified facts"):
 *  - every `*_ts` / `ts` / `acknowledged_at` is epoch MILLISECONDS (number), never ISO --
 *    and so are the envelope's `window.from/to` and path-window's `from/to`
 *    (`alert_analytics.py`'s `Filters.window()` / `_ms`): the request sends ISO-8601,
 *    the response answers in ms like every other instant on the wire;
 *  - day buckets are `YYYY-MM-DD` in IST;
 *  - nullable numeric metrics (`km`, `alerts_per_100km`, medians) are `null`, not 0,
 *    when the site has no trips/paths to compute them from (Wonder Cement today).
 */

export type AlertSite = 'ultratech' | 'wondercement'
export const ALERT_SITES: readonly AlertSite[] = ['ultratech', 'wondercement']

export type AlertSeverity = 'INFO' | 'WARN' | 'CRITICAL'
export const ALERT_SEVERITIES: readonly AlertSeverity[] = ['CRITICAL', 'WARN', 'INFO']

/** 22:00-06:00 IST is night, matching `started_at_night` in db/reports_driver_tims.sql. */
export type AlertShift = 'day' | 'night'

export type TrendGrain = 'day' | 'week' | 'hour' | 'dow_hour'
export type TrendDimension = 'kind' | 'severity' | 'driver' | 'vehicle' | 'location' | 'shift'
export type AlertListSort = 'ts_desc' | 'ts_asc' | 'severity'

/** Every GET under /api/reports/alerts answers in this shape; `filters` echoes the parsed
 *  query so a response can be matched to the request that produced it. */
export interface AnalyticsEnvelope<T> {
  report: string
  site: AlertSite
  /** Epoch ms, [from, to). */
  window: { from: number; to: number }
  filters: Record<string, unknown>
  rows: T[]
  count: number
}

/* ------------------------------------------------------------------ aggregations */

export interface ByDriverRow {
  /** Driver name, or the literal 'unassigned' when no TIMS spell covered the alert. */
  driver_label: string
  driver_id: string | null
  alerts: number
  critical: number
  warn: number
  vehicles: number
  kinds: number
  top_kind: string | null
  night_alerts: number
  first_ts: number | null
  last_ts: number | null
  /** Completed-trip km in the window; null when < 25 km or the site records no trips. */
  km: number | null
  alerts_per_100km: number | null
}

export interface ByVehicleRow {
  vehicle_id: string
  alerts: number
  critical: number
  warn: number
  drivers: number
  kinds: number
  top_kind: string | null
  night_alerts: number
  first_ts: number | null
  last_ts: number | null
  km: number | null
  alerts_per_100km: number | null
}

export interface ByLocationRow {
  /** hub_code, or 'en route' between hubs. */
  place: string
  /** overspeed_zone() label; null outside any zone. */
  zone: string | null
  alerts: number
  critical: number
  vehicles: number
  drivers: number
  top_kind: string | null
  median_speed_kph: number | null
  median_limit_kph: number | null
  /** Centroid of the lat/lon bin (bin_deg query param, default 0.01). */
  lat: number | null
  lon: number | null
}

export interface ByShiftRow {
  shift: AlertShift
  kind: string
  alerts: number
  critical: number
  vehicles: number
  drivers: number
  /** Share of this kind within its own shift, 0-100. */
  share_pct: number
}

export interface TrendRow {
  /** 'YYYY-MM-DD' (day), ISO-week Monday 'YYYY-MM-DD' (week), '00'..'23' (hour) or
   *  '<dow>-<hh>' with dow 0 = Monday (dow_hour). */
  bucket: string
  /** Top-N value of the chosen dimension, or the literal 'other'. */
  dim_value: string
  alerts: number
  critical: number
}

export interface TrendEnvelope extends AnalyticsEnvelope<TrendRow> {
  grain: TrendGrain
  dimension: TrendDimension
}

/* ------------------------------------------------------------------------ facets */

export interface FacetRow {
  facet: 'kind' | 'severity' | 'hub' | 'shift' | 'driver' | 'vehicle' | string
  value: string
  n: number
}

/** One pre-split facet entry; structurally a subset of FacetRow so either shape fits. */
export interface FacetValue {
  value: string
  n: number
}

export interface FacetsEnvelope extends AnalyticsEnvelope<FacetRow> {
  kinds: FacetValue[]
  severities: FacetValue[]
  /** hub_code values plus 'en route'. */
  hubs: FacetValue[]
  drivers: FacetValue[]
  vehicles: FacetValue[]
}

/* -------------------------------------------------------------------- alert rows */

/** The minimum an alert needs to be plotted and labelled on `map/TripMap.tsx`; every
 *  `AlertBaseRow` is one, so the drawer hands its rows straight to the map. */
export interface TripMapAlert {
  alert_id: string
  kind: string
  severity: AlertSeverity
  ts: number
  lat: number | null
  lon: number | null
  message: string | null
}

/** The columns `f_alert_base` returns -- the deduped alert with its spell-attributed
 *  driver, place/zone and IST calendar fields. */
export interface AlertBaseRow extends TripMapAlert {
  /** Last occurrence folded into this deduped row (== ts when occurrence_count is 1). */
  last_ts: number
  occurrence_count: number
  /** null only for cell-based (congestion) alerts, which name no vehicle -- the base scan
   *  LEFT JOINs `vehicle`, so they survive the fleet filter as Ultratech. */
  vehicle_id: string | null
  fleet: string | null
  site: AlertSite
  hub_code: string | null
  place: string
  zone: string | null
  route_id: string | null
  detail: Record<string, unknown> | null
  acknowledged_at: number | null
  acknowledged_by: string | null
  driver_id: string | null
  driver_name: string | null
  driver_label: string
  ist_hour: number
  /** 0 = Monday .. 6 = Sunday. */
  ist_dow: number
  ist_day: string
  shift: AlertShift
}

export interface AlertListRow extends AlertBaseRow {
  /** Containing trip (half-open [started_ts, ended_ts) on the same vehicle), if any. */
  trip_id: string | null
  round_trip_id: string | null
  trip_started_ts: number | null
  trip_ended_ts: number | null
  origin_hub: string | null
  destination_hub: string | null
  load_state: string | null
  /** COUNT(*) OVER () -- the unpaged total, repeated on every row. */
  total_count: number
}

export interface AlertListEnvelope extends AnalyticsEnvelope<AlertListRow> {
  total: number
  limit: number
  offset: number
}

/* ------------------------------------------------------------------ alert -> trip */

export interface AlertTrip {
  /** null for a geo-leg (v_leg_from_geo has no id -- the drawer falls back to path-window). */
  trip_id: string | null
  round_trip_id: string | null
  vehicle_id: string
  route_id: string | null
  load_state: string | null
  origin_hub: string | null
  destination_hub: string | null
  started_ts: number
  ended_ts: number | null
  distance_odo_km: number | null
  completed: boolean
  source: 'trip' | 'geo_leg'
}

export interface AlertRoundTrip {
  round_trip_id: string | null
  origin_hub: string | null
  far_hub: string | null
  started_ts: number | null
  ended_ts: number | null
}

export interface AlertTripDriver {
  driver_id: string | null
  driver_label: string
}

/** GET /api/reports/alerts/{alert_id}/trip */
export interface AlertTripEnvelope {
  alert: AlertBaseRow
  site: AlertSite
  trip: AlertTrip | null
  round_trip: AlertRoundTrip | null
  driver: AlertTripDriver
  /** Every deduped alert inside the trip's window (`f_alert_trip_alerts`); empty when
   *  trip is null -- the drawer re-adds the opened alert itself. */
  alerts: AlertBaseRow[]
}

/* ------------------------------------------------------------------- path window */

export interface PathWindowPoint {
  ts: number
  lat: number
  lon: number
  speed_kph: number | null
  heading_deg: number | null
  soc_pct: number | null
}

/** GET /api/reports/alerts/path-window?vehicle_id&from&to&max_points */
export interface PathWindowResponse {
  vehicle_id: string
  site: AlertSite
  /** Epoch ms -- the parsed request window echoed back, not the ISO strings sent. */
  from: number
  to: number
  points: PathWindowPoint[]
  point_count: number
  /** true when more raw points existed than max_points and a stride was applied. */
  decimated: boolean
  /** Every n-th raw point kept (1 when not decimated). */
  stride: number
}
